import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { CarType } from '../../enum';
import { DealerInventoryOption } from '../../types/dealer-inventory-options';
import { WidgetConfig } from '../../types';
import WidgetAPI from '../../services/api';
import { getAssetURL } from '../../utils/url';
import { getAnonymousToken, getAuthToken } from '../../utils/storage';
import {
  isAuthTokenExpired,
  IsNotNullOrEmpty,
  IsNullOrEmpty,
} from '../../utils/validation';
import Loader from '../Loader';
import debounce from 'lodash/debounce';

interface VehicleListItem {
  id: string;
  title: string;
  stock: string;
  miles?: string;
  imageUrl: string;
}

interface CarInventoryDialogProps {
  config: WidgetConfig;
  carType: CarType;
  onCarSelected: (carData: any) => void;
  onBack?: () => void;
  open: boolean;
  onClose: () => void;
}

const CarInventoryDialog: React.FC<CarInventoryDialogProps> = ({
  config,
  carType,
  onCarSelected,
  onBack,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [api] = useState(() => new WidgetAPI(config));

  const [activeView, setActiveView] = useState<CarType>(carType);
  const [selectedBodyType, setSelectedBodyType] = useState('');
  const [selectedPriceRange, setSelectedPriceRange] = useState<{
    minPrice?: number;
    maxPrice?: number;
    label?: string;
  }>({});
  const [searchQuery, setSearchQuery] = useState('');

  const [inventoryOptions, setInventoryOptions] = useState<
    DealerInventoryOption[]
  >([]);
  const [selectedInventoryOption, setSelectedInventoryOption] = useState<
    DealerInventoryOption | undefined
  >(undefined);
  const [newVehicles, setNewVehicles] = useState<VehicleListItem[]>([]);
  const [usedVehicles, setUsedVehicles] = useState<VehicleListItem[]>([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const fetchingRef = useRef(false);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value);
  };

  const handleSearchByDebounce = debounce(handleSearch, 700);

  const usedCategories = useMemo(
    () => [
      {
        name: 'Sedan',
        image: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/sedan.png',
        code: 'sedan',
      },
      {
        name: 'Truck',
        image: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/truck.png',
        code: 'truck',
      },
      {
        name: 'Coupe',
        image: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/coupe.png',
        code: 'coupe',
      },
      {
        name: 'Minivan',
        image: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/minivan.png',
        code: 'minivan',
      },
      {
        name: 'Electric',
        image: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/electric.png',
        code: 'electric',
      },
      { name: 'SUV', image: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/suv.png', code: 'suv' },
      {
        name: 'Convertible',
        image: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/convertible.png',
        code: 'convertible',
      },
      {
        name: 'Crossover',
        image: 'https://miles-cdn.qrbuddy.ai/static/autospotlight/crossover.png',
        code: 'crossover',
      },
    ],
    [],
  );

  const usedPricing = useMemo(
    () => [
      { label: 'Under $20k', minPrice: undefined, maxPrice: 20000 },
      { label: 'Under $30k', minPrice: undefined, maxPrice: 30000 },
      { label: 'Under $40k', minPrice: undefined, maxPrice: 40000 },
      { label: 'Under $50k+', minPrice: undefined, maxPrice: 50000 },
      { label: '$50k+', minPrice: 50000, maxPrice: undefined },
    ],
    [],
  );

  useEffect(() => {
    const fetchInventoryOptions = async () => {
      if (fetchingRef.current) return;

      if (activeView === CarType.new) {
        // if (inventoryOptions.length <= 0) {
          try {
            fetchingRef.current = true;

            await anonymousUserLogin();

            const result = await api.getDealerInventoryOptions({
              carType: 'new',
              ...(selectedPriceRange.minPrice !== undefined && {
                minPrice: selectedPriceRange.minPrice,
              }),
              ...(selectedPriceRange.maxPrice !== undefined && {
                maxPrice: selectedPriceRange.maxPrice,
              }),
            });

            if (result?.data) {
              setInventoryOptions(result.data);
            }
          } catch (error) {
            console.error(error);
          } finally {
            fetchingRef.current = false;
          }
        // }
      }
    };

    // setActiveView(carType);
    fetchInventoryOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, carType, selectedPriceRange.minPrice, selectedPriceRange.maxPrice]);

  useEffect(() => {
    const mapVehicle = (vehicle: any): VehicleListItem => {
      const photo = vehicle?.photos?.[0];
      const imageUrl = photo
        ? getAssetURL(config.assetsUrl, photo?.key, photo?.url)
        : `${config?.webUrl}/static/car_placeholder.png`;
      return {
        id: vehicle?.id ?? '',
        title: vehicle?.title ?? 'Vehicle',
        stock: vehicle?.stockNumber ?? '',
        miles: vehicle?.mileage ? `${vehicle.mileage} miles` : undefined,
        imageUrl: imageUrl ?? '',
      };
    };

    const extractVehicles = (response: any): any[] => {
      if (Array.isArray(response?.data)) return response.data;
      if (Array.isArray(response?.data?.data)) return response.data.data;
      if (Array.isArray(response)) return response;
      return [];
    };

    const fetchCars = async () => {
      setIsLoadingVehicles(true);
      try {
        await anonymousUserLogin();
        if (activeView === CarType.new) {
          if (searchQuery.trim().length === 0 && !selectedInventoryOption) {
            setNewVehicles([]);
            return;
          }
          const response = await api.getVehicles({
            dealerId: config.dealerId,
            carBrandId: selectedInventoryOption?.carBrandId,
            carModelId: selectedInventoryOption?.carModelId,
            carType: 'new',
            ...(searchQuery.trim().length > 0 && {
              search: searchQuery.trim(),
            }),
            ...(selectedPriceRange.minPrice !== undefined && {
              minPrice: selectedPriceRange.minPrice,
            }),
            ...(selectedPriceRange.maxPrice !== undefined && {
              maxPrice: selectedPriceRange.maxPrice,
            }),
          });
          const vehicles = extractVehicles(response);
          setNewVehicles(vehicles.map(mapVehicle));
        } else if (activeView === CarType.used) {
          const response = await api.getVehicles({
            dealerId: config.dealerId,
            carType: 'used',
            ...(IsNotNullOrEmpty(selectedBodyType) && {
              bodyType: selectedBodyType,
            }),
            ...(searchQuery.trim().length > 0 && {
              search: searchQuery.trim(),
            }),
            ...(selectedPriceRange.minPrice !== undefined && {
              minPrice: selectedPriceRange.minPrice,
            }),
            ...(selectedPriceRange.maxPrice !== undefined && {
              maxPrice: selectedPriceRange.maxPrice,
            }),
            ...(!IsNotNullOrEmpty(selectedBodyType) &&
              searchQuery.trim().length === 0 && { limit: 3 }),
          });
          const vehicles = extractVehicles(response);
          setUsedVehicles(vehicles.map(mapVehicle));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingVehicles(false);
      }
    };

    fetchCars();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeView,
    selectedInventoryOption,
    selectedBodyType,
    selectedPriceRange.minPrice,
    selectedPriceRange.maxPrice,
    searchQuery,
    api,
    config,
  ]);

  const anonymousUserLogin = async () => {
    if (!api.isAuthenticated()) {
      const authToken = getAuthToken();
      let anonymousAuthToken = getAnonymousToken();
      if (
        IsNullOrEmpty(authToken) &&
        (IsNullOrEmpty(anonymousAuthToken) ||
          isAuthTokenExpired(anonymousAuthToken))
      ) {
        await api.anonymousLogin();
      }
    }
  };

  const handleInventoryOptionClick = async (option: DealerInventoryOption) => {
    if (
      selectedInventoryOption?.carBrandId === option.carBrandId &&
      selectedInventoryOption?.carModelId === option.carModelId
    ) {
      setSelectedInventoryOption(undefined);
      return;
    }
    setSelectedInventoryOption(option);
  };

  const handleCarTypeClick = async (code: string) => {
    if (selectedBodyType === code) {
      setSelectedBodyType('');
      return;
    }
    setSelectedBodyType(code);
  };

  const getInventoryOptionImageUrl = (vehicleType: DealerInventoryOption) => {
    if (vehicleType?.heroPhoto?.key) {
      return getAssetURL(
        config.assetsUrl,
        vehicleType.heroPhoto.key,
        vehicleType.heroPhoto.url,
      );
    }

    return (
      usedCategories.find(
        (item) =>
          item.code.toLowerCase() === vehicleType.bodyType?.toLowerCase(),
      )?.image || `${config?.webUrl}/static/car_placeholder.png`
    );
  };

  const getInventoryOptionLabel = (vehicleType: DealerInventoryOption) => {
    const fallbackTitle = [
      vehicleType.carBrandTitle,
      vehicleType.carModelTitle,
      vehicleType.bodyType,
    ]
      .filter(Boolean)
      .join(' ');

    return (vehicleType.displayTitle || fallbackTitle || 'Vehicle').trim();
  };

  const renderPriceFilter = () => {
    return (
      <Grid container spacing={1}>
        {usedPricing.map((price) => (
          <Grid key={price.label} size={{ xs: 4, sm: 2.4 }}>
            <Chip
              label={price.label}
              size='small'
              onClick={() =>
                setSelectedPriceRange((prev) =>
                  prev.label === price.label
                    ? {}
                    : {
                        label: price.label,
                        minPrice: price.minPrice,
                        maxPrice: price.maxPrice,
                      },
                )
              }
              sx={{
                width: '100%',
                bgcolor:
                  selectedPriceRange.label === price.label
                    ? '#d6e7ff'
                    : '#f5f6fa',
                color: '#47515c',
                fontWeight: 600,
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth='md'
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#E6E8EF',
          backgroundImage: 'none',
          maxHeight: isMobile ? '100vh' : '95vh',
          m: isMobile ? 0 : 2,
          borderRadius: isMobile ? 0 : 3,
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          minHeight: '70vh',
          maxWidth: 900,
          mx: 'auto',
          borderRadius: 2,
          p: { xs: 1.5, sm: 2.5 },
          background: 'linear-gradient(180deg, #f7f7fb, #e1e3ea)',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
        }}
      >
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '40px 1fr 40px',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {onBack ? (
              <IconButton
                onClick={onBack}
                aria-label='Back'
                sx={{
                  bgcolor: 'rgba(0,0,0,0.04)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                }}
              >
                <ChevronLeft />
              </IconButton>
            ) : (
              <Box />
            )}

            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 18, sm: 20 },
                  color: '#2b2f36',
                  lineHeight: 1.2,
                }}
              >
                {activeView === CarType.new ? 'New Vehicles' : 'Used Vehicles'}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 13 },
                  color: '#7a8392',
                  mt: 0.5,
                }}
              >
                {activeView === CarType.new
                  ? 'Choose a model to explore'
                  : 'Narrow your search'}
              </Typography>
            </Box>

            <IconButton
              onClick={() =>
                setActiveView((prev) =>
                  prev === CarType.new ? CarType.used : CarType.new,
                )
              }
              aria-label='Next'
              sx={{
                justifySelf: 'end',
                bgcolor: 'rgba(0,0,0,0.04)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          <TextField
            size='small'
            placeholder={
              activeView === CarType.new
                ? 'Search model or body style'
                : 'Search make, model, or keyword'
            }
            fullWidth
            // value={searchQuery}
            // onChange={(event) => setSearchQuery(event.target.value)}
            onChange={handleSearchByDebounce}
            sx={{
              bgcolor: '#f2f3f7',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#f2f3f7',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ color: '#9aa1ad' }} fontSize='small' />
                </InputAdornment>
              ),
            }}
          />

          {activeView === CarType.new && renderPriceFilter()}

          {activeView === CarType.new ? (
            <>
              {/* Show static image for qrdemos.com (Sterling Auto Group) otherwise show vehicle data */}
              {false && config.dealerId === 'b6b848d5-3ba4-4c96-a567-a28f45504ce8'
              ? <Box component={'img'} src='https://miles-cdn.qrbuddy.ai/static/autospotlight/img_shop_by_model_cadillac.png' sx={{width: '100%', height: '100%'}}/>
              : <Grid container spacing={1.5}>
                {fetchingRef.current || isLoadingVehicles ? (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Loader />
                  </Box>
                ) : newVehicles.length > 0 ? (
                  newVehicles.map((vehicle) => {
                    return (
                      <Grid key={vehicle.id} size={{ xs: 6, sm: 3 }}>
                        <Box
                          onClick={() => onCarSelected(vehicle)}
                          sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: '1px solid rgba(0,0,0,0.1)',
                            bgcolor: '#ffffff',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
                            cursor: 'pointer',
                            textAlign: 'center',
                            height: '100%',
                          }}
                        >
                          <Box
                            sx={{
                              height: { xs: 80, sm: 90 },
                              backgroundImage: `url(${vehicle.imageUrl})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                          <Box sx={{ p: 1 }}>
                            <Typography
                              // noWrap
                              sx={{
                                fontWeight: 700,
                                color: '#3a3f47',
                                fontSize: { xs: 12, sm: 13 },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: '2', // Limit to 2 lines
                                WebkitBoxOrient: 'vertical',
                                
                              }}
                            >
                              {vehicle.title}
                            </Typography>
                            <Typography
                              sx={{
                                color: '#7a808a',
                                fontSize: { xs: 11, sm: 12 },
                              }}
                            >
                              {vehicle.stock}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      borderRadius: 3,
                      bgcolor: '#020202',
                      px: { xs: 1.5, sm: 2.5 },
                      py: { xs: 2, sm: 2.5 },
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#f5f7fb',
                        fontSize: { xs: 18, sm: 22 },
                        fontWeight: 500,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        mb: 2,
                      }}
                    >
                      Shop by Model
                    </Typography>

                    <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
                      {inventoryOptions.map((vehicleType) => {
                        const isVehicleTypeSelected =
                          selectedInventoryOption?.carBrandId ===
                            vehicleType.carBrandId &&
                          selectedInventoryOption?.carModelId ===
                            vehicleType.carModelId;

                        return (
                          <Grid
                            key={`${vehicleType.displayTitle}`}
                            size={{ xs: 6, sm: 4 }}
                            sx={{ display: 'flex' }}
                          >
                            <Box
                              onClick={() =>
                                handleInventoryOptionClick(vehicleType)
                              }
                              sx={{
                                height: { xs: 156, sm: 172 },
                                maxWidth: { xs: 180, sm: 215 },
                                mx: 'auto',
                                width: '100%',
                                borderRadius: '22px',
                                border: isVehicleTypeSelected
                                  ? '2px solid rgba(101, 179, 255, 0.95)'
                                  : '1.5px solid rgba(255,255,255,0.62)',
                                bgcolor: '#050505',
                                boxShadow: isVehicleTypeSelected
                                  ? '0 0 0 2px rgba(39,128,245,0.18)'
                                  : 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                transition:
                                  'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  borderColor: 'rgba(255,255,255,0.9)',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  flex: '0 0 auto',
                                  height: { xs: 112, sm: 124 },
                                  backgroundImage: `url(${getInventoryOptionImageUrl(vehicleType)})`,
                                  backgroundSize: getInventoryOptionImageUrl(vehicleType)?.includes('/static/') ? '100% auto' : 'cover',
                                  backgroundPosition: 'center',
                                  backgroundRepeat: 'no-repeat',
                                }}
                                aria-label={getInventoryOptionLabel(vehicleType)}
                              />

                              <Box
                                sx={{
                                  p: { xs: 1, sm: 1.25 },
                                  height: { xs: 44, sm: 48 },
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: '#f4f6fb',
                                    fontSize: { xs: 11, sm: 14 },
                                    fontWeight: 500,
                                    lineHeight: 1.14,
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2,
                                    minHeight: { xs: 26, sm: 32 },
                                  }}
                                >
                                  {getInventoryOptionLabel(vehicleType)}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </Grid>}
              {!fetchingRef.current &&
                !isLoadingVehicles &&
                selectedInventoryOption &&
                newVehicles.length === 0 && (
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontSize: 12,
                      color: '#7b828e',
                      mt: 1,
                    }}
                  >
                    No vehicles found for this selection.
                  </Typography>
                )}

              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: 13,
                  color: '#6a727f',
                  mt: 1,
                }}
              >
                Click on Vehicle to View
              </Typography>
            </>
          ) : (
            <Stack spacing={1.5}>
              <Grid container spacing={1}>
                {usedCategories.map((category) => (
                  <Grid key={category.code} size={{ xs: 3 }}>
                    {/* <Box
                      sx={{
                        borderRadius: 1.5,
                        bgcolor: '#f5f6fa',
                        border: '1px solid rgba(0,0,0,0.08)',
                        p: 0.75,
                        textAlign: 'center',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#4b525c',
                      }}
                    >
                      {category.name}
                    </Box> */}
                    <Card
                      onClick={() => handleCarTypeClick(category.code)}
                      sx={{
                        borderRadius: '12px',
                        p: category.code === selectedBodyType ? '3px' : '0',
                        border:
                          category.code === selectedBodyType
                            ? 'none'
                            : '1px solid #DBDBDB',
                        background:
                          category.code === selectedBodyType
                            ? '#2a80f5'
                            : 'transparent',
                        transition: 'all 0.3s ease',
                        boxShadow: 'none',
                        textAlign: 'center',
                        maxHeight: '150px',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          borderRadius: '10px',
                          background:
                            category.code === selectedBodyType
                              ? '#d6e7ff'
                              : '#E6E4EA',
                          height: '100%',
                          width: '100%',
                          p: 1,
                          position: 'relative',
                        }}
                      >
                        <Box
                          component='img'
                          src={category.image}
                          alt={category.name}
                          sx={{
                            width: '100%',
                            maxWidth: { xs: '200px', md: '250px' },
                            height: 'auto',
                            objectFit: 'contain',
                            mx: 'auto',
                            mb: 0.5,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: { xs: '11px', sm: '14px' },
                            fontWeight: 600,
                            color: '#000',
                            lineHeight: '12px',
                          }}
                        >
                          {category.name}
                        </Typography>

                        {category.code === selectedBodyType && (
                          <CheckCircleIcon
                            color='primary'
                            sx={{
                              fontSize: { xs: '12px', sm: '16px' },
                              position: 'absolute',
                              top: 2,
                              right: 2,
                            }}
                          />
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {renderPriceFilter()}

              <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />

              <Stack spacing={1}>
                {isLoadingVehicles ? (
                  <Loader />
                ) : usedVehicles.length > 0 ? (
                  usedVehicles.map((vehicle) => (
                    <Box
                      key={vehicle.id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '72px 1fr auto',
                        gap: 1,
                        alignItems: 'center',
                        bgcolor: '#f9fafc',
                        borderRadius: 1.5,
                        border: '1px solid rgba(0,0,0,0.08)',
                        p: 1,
                      }}
                    >
                      <Box
                        sx={{
                          height: 48,
                          borderRadius: 1,
                          backgroundImage: `url(${vehicle.imageUrl})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          border: '1px solid rgba(0,0,0,0.08)',
                        }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: '#2e3238',
                          }}
                        >
                          {vehicle.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#7b828e' }}>
                          {vehicle.stock}
                        </Typography>
                      </Box>
                      <Stack spacing={0.25} alignItems='flex-end'>
                        <Button
                          variant='contained'
                          size='small'
                          onClick={() => onCarSelected(vehicle)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: '#2a80f5',
                            '&:hover': { bgcolor: '#1d6fe0' },
                            minWidth: 68,
                            px: 1,
                          }}
                        >
                          Select
                        </Button>
                        <Typography sx={{ fontSize: 10, color: '#7b828e' }}>
                          {vehicle.miles}
                        </Typography>
                      </Stack>
                    </Box>
                  ))
                ) : (
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontSize: 12,
                      color: '#7b828e',
                      py: 2,
                    }}
                  >
                    No vehicles found.
                  </Typography>
                )}
              </Stack>

              <Button
                fullWidth
                variant='contained'
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: '#5b6b8d',
                  '&:hover': { bgcolor: '#4d5c7c' },
                  borderRadius: 1.5,
                  py: 1,
                }}
              >
                Click Select to View Vehicle
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
};

export default CarInventoryDialog;
